import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthFlowService } from '../services/auth-flow/auth-flow.service';
import { AuthService } from '../common/auth/src/services/auth.service';
import { LoggedUserService } from '../common/auth/src/services/logged-user.service';
import { map, take, switchMap, timeout, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

/**
 * Guard for application routes (all internal pages)
 * Ensures users are authenticated and have completed the auth flow
 */
export const appGuard: CanActivateFn = () => {
  const authFlowService = inject(AuthFlowService);
  const authService = inject(AuthService);
  const loggedUserService = inject(LoggedUserService);
  const router = inject(Router);
  
  // Check authentication status from Cognito
  return authService.isAuthenticated$().pipe(
    take(1),
    switchMap((isAuthenticated) => {
      // If not authenticated with Cognito, redirect to auth flow
      if (!isAuthenticated) {
        return of(router.createUrlTree(['/']));
      }
      
      // If authenticated, we need to ensure user data and role are loaded
      // before checking if flow is completed (especially after page refresh)
      if (!authFlowService.userData()) {
        // Check if user creation polling is already in progress
        // If yes, skip loading to avoid duplicate API calls
        if (authFlowService.isPollingUserCreation()) {
          console.log('[appGuard] User creation polling in progress, redirecting to auth flow');
          return of(router.createUrlTree(['/']));
        }

        // ✅ HIGH FIX: Add 10s timeout to prevent guard from hanging
        // Load user data first
        return loggedUserService.getUserIdentifiers$().pipe(
          take(1),
          timeout(10000), // 10 seconds timeout for guard
          switchMap(identifiers => {
            // Set role
            if (identifiers.group) {
              authFlowService.saveUserRole(identifiers.group.toLowerCase());
            }
            // Load user data
            return loggedUserService.getUser(false).pipe(
              take(1),
              timeout(10000), // 10 seconds timeout for user data load
              map(userData => {
                if (userData) {
                  authFlowService.saveUserData(userData);
                }
                // Now check if flow is completed with loaded data
                if (!authFlowService.isCompleted()) {
                  return router.createUrlTree(['/']);
                }
                return true;
              })
            );
          }),
          catchError(error => {
            // ✅ HIGH FIX: Handle timeout and errors gracefully
            console.error('[appGuard] Error or timeout loading user data:', error);
            // Redirect to auth flow on error/timeout
            return of(router.createUrlTree(['/']));
          })
        );
      }
      
      // User data already loaded, just check if flow is completed
      if (!authFlowService.isCompleted()) {
        return of(router.createUrlTree(['/']));
      }
      
      return of(true);
    })
  );
};